import React from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Container, Row, Col, Badge } from 'reactstrap';
import useGetTagByName from '../../api/useGetTagByName';
import useGetChildTags from '../../api/useGetChildTags';
import useGetTagHierarchy from '../../api/useGetTagHierarchy';
import useGetQuestionsAndArticlesByCategory from '../../api/useGetQuestionsAndArticlesByCategory';
import DocumentHead from '../shared/DocumentHead';
import Header from '../shared/Header';
import LoadingSpinner from '../shared/LoadingSpinner';
import useTrackPageView from '../../hooks/useTrackPageView';
import {
    Tag,
    ArticleListItemModel,
    Question,
    CategorisedQuestionsAndArticles
} from '../../types';
import { createUrlFromQuestion } from '../knowledge/createUrlFromQuestion';

type ContentFilterType = 'all' | 'articles' | 'questions';

const TagViewPage: React.FC = () => {
    useTrackPageView();

    // Use URL search params for content filter
    const [searchParams, setSearchParams] = useSearchParams();
    const filterParam = searchParams.get('filter');
    const contentFilter: ContentFilterType =
        filterParam === 'articles' || filterParam === 'questions'
            ? filterParam
            : 'all';

    const { tagName } = useParams<{ tagName: string }>();

    // Function to set filter via URL params
    const handleSetFilter = (filter: ContentFilterType) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (filter === 'all') {
            newSearchParams.delete('filter');
        } else {
            newSearchParams.set('filter', filter);
        }
        setSearchParams(newSearchParams);
    };

    const getTagByName = useGetTagByName({ tagName: tagName || '' });
    const {
        data: tag,
        error: tagError,
        isLoading: tagLoading
    } = useQuery(['tag', tagName], getTagByName, {
        enabled: !!tagName
    });

    // Get child tags
    const getChildTags = useGetChildTags({ tagId: tag?.id || '123' });

    const {
        data: childTags,
        isLoading: childTagsLoading,
        error: childTagsError
    } = useQuery(['childTags', tag?.id], getChildTags, {
        enabled: !!tag?.id
    });

    // Get tag hierarchy for breadcrumb
    const getTagHierarchy = useGetTagHierarchy({ tagId: tag?.id || '' });
    const {
        data: tagHierarchy,
        isLoading: hierarchyLoading,
        error: hierarchyError
    } = useQuery(['tagHierarchy', tag?.id], getTagHierarchy, {
        enabled: !!tag?.id
    });

    // Get articles and questions for this tag
    const getQuestionsAndArticles = useGetQuestionsAndArticlesByCategory({
        tagText: tagName || ''
    });
    const {
        data: questionsAndArticles,
        isLoading: contentLoading,
        error: contentError
    } = useQuery(
        ['questionsAndArticlesByCategory', tagName],
        getQuestionsAndArticles,
        {
            enabled: !!tagName
        }
    );

    if (!tagName) {
        return <div>Topic name is required</div>;
    }

    if (tagError || contentError || (!tagLoading && !tag)) {
        return (
            <>
                <DocumentHead title=' - Topic Not Found' />
                <Header headerClassName='mini' />
                <section className='white-bg py-5'>
                    <Container>
                        <Row>
                            <Col lg={8} md={10} className='mx-auto'>
                                <div className='custom-card answer-card article-card mt-3 card tag-view-page'>
                                    <div className='tag-error-container text-center py-5'>
                                        <div className='error-icon mb-4'>
                                            <svg
                                                width='64'
                                                height='64'
                                                viewBox='0 0 24 24'
                                                fill='none'
                                                stroke='currentColor'
                                                strokeWidth='1.5'
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                                className='text-muted'
                                            >
                                                <circle
                                                    cx='12'
                                                    cy='12'
                                                    r='10'
                                                ></circle>
                                                <line
                                                    x1='12'
                                                    y1='8'
                                                    x2='12'
                                                    y2='12'
                                                ></line>
                                                <line
                                                    x1='12'
                                                    y1='16'
                                                    x2='12.01'
                                                    y2='16'
                                                ></line>
                                            </svg>
                                        </div>
                                        <h2 className='mb-3'>
                                            Unable to Load Topic
                                        </h2>
                                        <p className='text-muted mb-4'>
                                            Something went wrong and we can't
                                            find this topic.
                                        </p>
                                        <div className='d-flex flex-column flex-sm-row justify-content-center gap-3'>
                                            <Link
                                                to='/'
                                                className='btn btn-primary'
                                            >
                                                Go to Home Page
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </>
        );
    }

    return (
        <>
            <DocumentHead
                title={` - ${tag?.text}`}
                description={`Articles related to the topic: ${tag?.text}`}
            />
            <Header headerClassName='mini' />
            <section className='white-bg py-5'>
                <Container>
                    <Row>
                        {/* Main Content */}
                        <Col lg={10} md={8} className='mx-auto'>
                            <div className='custom-card answer-card article-card mt-3 card tag-view-page'>
                                <TagBreadcrumb
                                    hierarchy={tagHierarchy || []}
                                    loading={hierarchyLoading}
                                    error={hierarchyError}
                                />
                                <TagMainContent
                                    tag={tag}
                                    childTags={childTags || []}
                                    childTagsLoading={childTagsLoading}
                                    childTagsError={childTagsError}
                                    questionsAndArticles={
                                        questionsAndArticles || undefined
                                    }
                                    contentLoading={contentLoading}
                                    contentError={contentError !== null}
                                    contentFilter={contentFilter}
                                    onSetFilter={handleSetFilter}
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </>
    );
};

// Breadcrumb Component
const TagBreadcrumb: React.FC<{
    hierarchy: Tag[];
    loading: boolean;
    error: unknown;
}> = ({ hierarchy, loading, error }) => {
    if (loading) {
        return <div className='text-muted'>Loading breadcrumb...</div>;
    }

    if (error || hierarchy.length <= 1) {
        return null;
    }

    return (
        <nav aria-label='breadcrumb'>
            <ol className='breadcrumb'>
                {hierarchy.map((tag, index) => (
                    <li
                        key={tag.id}
                        className={`breadcrumb-item ${
                            index === hierarchy.length - 1 ? 'active' : ''
                        }`}
                        {...(index === hierarchy.length - 1
                            ? { 'aria-current': 'page' }
                            : {})}
                    >
                        {index === hierarchy.length - 1 ? (
                            tag.text
                        ) : (
                            <Link to={`/topic/${encodeURIComponent(tag.text)}`}>
                                {tag.text}
                            </Link>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
};

export const FeaturedImage: React.FC<{ tag: Tag }> = ({ tag }) => {
    if (tag.featuredImageUrl) {
        return (
            <img
                src={tag.featuredImageUrl}
                alt={tag.text}
                className='featured-image'
            />
        );
    }
    if (tag.fontAwesomeIcon) {
        return (
            <i
                className={`${tag.fontAwesomeIcon} featured-icon`}
                aria-hidden='true'
            />
        );
    }
    return (
        <div className='featured-placeholder'>
            {tag.text.charAt(0).toUpperCase()}
        </div>
    );
};

// Main Content Component
const TagMainContent: React.FC<{
    tag: Tag | undefined | null;
    childTags: Tag[];
    childTagsLoading: boolean;
    childTagsError: unknown;
    questionsAndArticles?: CategorisedQuestionsAndArticles;
    contentLoading: boolean;
    contentError: boolean;
    contentFilter: ContentFilterType;
    onSetFilter: (value: ContentFilterType) => void;
}> = ({
    tag,
    childTags,
    childTagsLoading,
    childTagsError,
    questionsAndArticles,
    contentLoading,
    contentError,
    contentFilter,
    onSetFilter
}) => {
    const hasQuestions = (questionsAndArticles?.questions?.length ?? 0) > 0;
    const hasArticles = (questionsAndArticles?.articles?.length ?? 0) > 0;
    const hasBothArticlesAndQuestions = hasArticles && hasQuestions;

    if (contentError) {
        return <div>Content error</div>;
    }

    if (contentLoading || !tag) {
        return (
            <div className='d-flex w-100 align-items-center justify-content-center'>
                <LoadingSpinner />
            </div>
        );
    }
    return (
        <div className='tag-content'>
            {/* Tag Info */}
            <div className='tag-header'>
                <div className='tag-icon-container'>
                    {tag.featuredImageUrl && (
                        <img src={tag.featuredImageUrl} alt={tag.text} />
                    )}
                    {tag.fontAwesomeIcon && (
                        <i
                            className={`${tag.fontAwesomeIcon}`}
                            aria-hidden='true'
                        />
                    )}
                </div>
                <div>
                    <h1 className='tag-title'>{tag.text}</h1>
                    {tag.description && (
                        <p className='text-muted tag-description'>
                            {tag.description}
                        </p>
                    )}
                </div>
            </div>

            {/* Content Filter Toggle */}
            {hasBothArticlesAndQuestions && (
                <div className='content-filter'>
                    <div className='filter-controls'>
                        <span className='filter-label'>Show content:</span>
                        <div
                            className='btn-group'
                            role='group'
                            aria-label='Content filter'
                        >
                            <button
                                type='button'
                                className={`btn ${
                                    contentFilter === 'all'
                                        ? 'btn-primary'
                                        : 'btn-outline-primary'
                                }`}
                                onClick={() => onSetFilter('all')}
                            >
                                All
                            </button>
                            <button
                                type='button'
                                className={`btn ${
                                    contentFilter === 'articles'
                                        ? 'btn-primary'
                                        : 'btn-outline-primary'
                                }`}
                                onClick={() => onSetFilter('articles')}
                            >
                                Articles
                            </button>
                            <button
                                type='button'
                                className={`btn ${
                                    contentFilter === 'questions'
                                        ? 'btn-primary'
                                        : 'btn-outline-primary'
                                }`}
                                onClick={() => onSetFilter('questions')}
                            >
                                Questions
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {contentLoading && <LoadingSpinner />}
            {contentError && (
                <div className='text-danger'>Error loading content</div>
            )}

            {/* Subtopics as Cards */}
            {childTags.length > 0 && (
                <div className='mb-5'>
                    <h3 className='section-title'>Sub-topics</h3>
                    <div className='subtopics-grid'>
                        {childTags
                            .toSorted((a, b) => {
                                return a.text.localeCompare(b.text);
                            })
                            .map((childTag) => {
                                const hasArticles =
                                    (childTag.articleCount ?? 0) > 0;
                                const hasSubTags =
                                    (childTag.subTagCount ?? 0) > 0;
                                const hasQuestions =
                                    (childTag.questionCount ?? 0) > 0;
                                if (
                                    !hasArticles &&
                                    !hasSubTags &&
                                    !hasQuestions
                                ) {
                                    return null;
                                }
                                if (
                                    contentFilter === 'articles' &&
                                    !(hasArticles || hasSubTags)
                                ) {
                                    return null;
                                }
                                if (
                                    contentFilter === 'questions' &&
                                    !hasQuestions
                                ) {
                                    return null;
                                }
                                return (
                                    <Link
                                        key={childTag.id}
                                        to={`/topic/${encodeURIComponent(
                                            childTag.text
                                        )}`}
                                        className='subtopic-card'
                                    >
                                        <div className='card h-100'>
                                            <div className='card-body d-flex align-items-start'>
                                                <div className='tag-icon me-3'>
                                                    <FeaturedImage
                                                        tag={childTag}
                                                    />
                                                </div>
                                                <div className='flex-grow-1 d-flex flex-column -100'>
                                                    <h6 className='card-title text-dark mb-2 fw-bold'>
                                                        {childTag.text}
                                                    </h6>
                                                    {childTag.description && (
                                                        <p className='card-text text-muted mb-3'>
                                                            {childTag.description.slice(
                                                                0,
                                                                80
                                                            )}
                                                            {childTag
                                                                .description
                                                                .length > 80 &&
                                                                '...'}
                                                        </p>
                                                    )}
                                                    <div
                                                        className={`badges ${
                                                            hasArticles &&
                                                            hasSubTags &&
                                                            hasQuestions
                                                                ? 'three-badges'
                                                                : ''
                                                        }`}
                                                        style={{
                                                            marginTop: 'auto'
                                                        }}
                                                    >
                                                        {hasSubTags && (
                                                            <Badge
                                                                pill
                                                                className='subtopics-badge'
                                                            >
                                                                {childTag.subTagCount ||
                                                                    0}{' '}
                                                                sub-topics
                                                            </Badge>
                                                        )}
                                                        {hasArticles && (
                                                            <Badge
                                                                // color='primary'
                                                                pill
                                                                className='articles-badge'
                                                            >
                                                                {childTag.articleCount ||
                                                                    0}{' '}
                                                                articles
                                                            </Badge>
                                                        )}
                                                        {hasQuestions && (
                                                            <Badge
                                                                pill
                                                                className='questions-badge'
                                                            >
                                                                {childTag.questionCount ||
                                                                    0}{' '}
                                                                questions
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className='ms-2 d-flex align-items-center'>
                                                    <svg
                                                        width='16'
                                                        height='16'
                                                        viewBox='0 0 24 24'
                                                        fill='none'
                                                        stroke='currentColor'
                                                        strokeWidth='2'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        className='text-muted'
                                                    >
                                                        <polyline points='9,18 15,12 9,6'></polyline>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                    </div>
                    {childTagsLoading && (
                        <div className='text-center mt-3'>
                            <div className='text-muted'>
                                Loading subtopics...
                            </div>
                        </div>
                    )}
                    {!!childTagsError && (
                        <div className='text-center mt-3'>
                            <div className='text-danger'>
                                Error loading subtopics
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Articles List */}
            {contentFilter !== 'questions' && hasArticles && (
                <Articles articles={questionsAndArticles!.articles} />
            )}

            {/* Questions List */}
            {contentFilter !== 'articles' && hasQuestions && (
                <Questions questions={questionsAndArticles?.questions} />
            )}

            {/* No content message when no children AND no visible content */}
            {questionsAndArticles &&
                childTags.length == 0 &&
                ((contentFilter === 'articles' && !hasArticles) ||
                    (contentFilter === 'questions' && !hasQuestions) ||
                    (contentFilter === 'all' &&
                        !hasArticles &&
                        !hasQuestions)) && (
                    <div className='text-center text-muted mt-5'>
                        <p>
                            {contentFilter === 'articles'
                                ? 'No articles found for this topic yet.'
                                : contentFilter === 'questions'
                                ? 'No questions found for this topic yet.'
                                : 'No articles or questions found for this topic yet.'}
                        </p>
                    </div>
                )}
        </div>
    );
};

const Articles: React.FC<{ articles: ArticleListItemModel[] }> = ({
    articles
}) => {
    const sortedArticles = articles.toSorted((a, b) => {
        return a.title.localeCompare(b.title);
    });
    const Article: React.FC<{ article: ArticleListItemModel }> = ({
        article
    }) => {
        return (
            <Link to={`/article/${article.slug}`} className='article-card-tvp'>
                <div className='card h-100'>
                    <div className='card-body'>
                        <div className='d-flex justify-content-between align-items-start'>
                            <div className='flex-grow-1'>
                                <h5 className='card-title text-dark mb-3 fw-bold'>
                                    {article.title}
                                </h5>
                                <p className='card-text text-muted mb-3'>
                                    {article.description}
                                </p>
                                {article.tags && article.tags.length > 0 && (
                                    <div className='article-tags'>
                                        {article.tags.map((tagText) => (
                                            <Badge
                                                color='primary'
                                                pill
                                                key={tagText}
                                                className='badge'
                                            >
                                                {tagText}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className='ms-3 d-flex align-items-center'>
                                <svg
                                    width='20'
                                    height='20'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='text-muted'
                                >
                                    <polyline points='9,18 15,12 9,6'></polyline>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        );
    };

    return (
        <div className='mb-5'>
            <h3 className='section-title'>Articles</h3>
            <div className='articles-grid'>
                {sortedArticles.map((article: ArticleListItemModel) => (
                    <Article key={article.id} article={article} />
                ))}
            </div>
        </div>
    );
};

const Questions: React.FC<{
    questions: Question[] | undefined;
}> = ({ questions }) => {
    return (
        <div className='questions-list'>
            <h3 className='section-title'>Related Questions</h3>
            <div>
                {questions?.map((question: Question) => (
                    <Link
                        key={question.questionId}
                        to={createUrlFromQuestion(
                            question.questionId,
                            question.text
                        )}
                        className='question-item'
                    >
                        <div className='question-content'>
                            <span className='question-text'>
                                {question.text.charAt(0).toUpperCase() +
                                    question.text.slice(1)}
                            </span>
                            <svg
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='question-arrow'
                            >
                                <polyline points='9,18 15,12 9,6'></polyline>
                            </svg>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};
export default TagViewPage;
